import React from "react";
import { RadixBadge } from "../primitives/RadixBadge";
import "./menuItemCard.css";
import { RadixButton } from "../primitives/RadixButton";
import { PlusIcon } from "@radix-ui/react-icons";
import { useMarketing } from "../../providers";
import { formatCurrency } from "../utility/formatCurrency";
import { shouldDisplayMenuEntity } from "../utility/menuItemVisibility";
var parseNumericValue = function (value) {
    if (typeof value === "number" && Number.isFinite(value)) {
        return value;
    }
    if (typeof value === "string") {
        var parsedValue = Number(value);
        return Number.isFinite(parsedValue) ? parsedValue : null;
    }
    return null;
};
var parsePricingMap = function (value) {
    if (!value) {
        return null;
    }
    if (typeof value === "string") {
        try {
            var parsedJson = JSON.parse(value);
            return parsePricingMap(parsedJson);
        }
        catch (_a) {
            return null;
        }
    }
    if (typeof value !== "object") {
        return null;
    }
    var parsedMapEntries = Object.entries(value).reduce(function (accumulator, _a) {
        var key = _a[0], mapValue = _a[1];
        var parsedMapValue = parseNumericValue(mapValue);
        if (parsedMapValue !== null) {
            accumulator[key] = parsedMapValue;
        }
        return accumulator;
    }, {});
    if (Object.keys(parsedMapEntries).length < 1) {
        return null;
    }
    return parsedMapEntries;
};
var getPriceByKey = function (pricingMap, key) {
    var directKey = String(key);
    if (Object.prototype.hasOwnProperty.call(pricingMap, directKey)) {
        return pricingMap[directKey];
    }
    var normalizedDecimalKey = Number(key).toFixed(2);
    if (Object.prototype.hasOwnProperty.call(pricingMap, normalizedDecimalKey)) {
        return pricingMap[normalizedDecimalKey];
    }
    return null;
};
var resolveSessionItemNavigation = function (menuId) {
    var session = window.session;
    var categories = Array.isArray(session === null || session === void 0 ? void 0 : session.categories) ? session.categories : [];
    for (var _i = 0, categories_1 = categories; _i < categories_1.length; _i++) {
        var category = categories_1[_i];
        var itemNavigations = Array.isArray(category === null || category === void 0 ? void 0 : category.item_navigations) ? category.item_navigations : [];
        var matchedNavigation = itemNavigations.find(function (itemNavigation) { return String(itemNavigation === null || itemNavigation === void 0 ? void 0 : itemNavigation.menu_id) === String(menuId); });
        if (matchedNavigation) {
            return matchedNavigation;
        }
    }
    return null;
};
var resolveSessionItemGroup = function (menuId) {
    var _a, _b;
    return (_b = (_a = resolveSessionItemNavigation(menuId)) === null || _a === void 0 ? void 0 : _a.item_group) !== null && _b !== void 0 ? _b : null;
};
var resolveBasePrice = function (levelPrice, matrixWeight, itemGroup, menuId) {
    var _a;
    var resolvedItemGroup = itemGroup !== null && itemGroup !== void 0 ? itemGroup : resolveSessionItemGroup(menuId);
    var itemGroupItems = Array.isArray(resolvedItemGroup === null || resolvedItemGroup === void 0 ? void 0 : resolvedItemGroup.items)
        ? resolvedItemGroup.items
        : [];
    var baseItem = ((_a = itemGroupItems.find(function (item) { return shouldDisplayMenuEntity(item); })) !== null && _a !== void 0 ? _a : null);
    if (!baseItem) {
        return null;
    }
    var baseItemPrice = parseNumericValue(baseItem.price);
    var baseItemLevel = parseNumericValue(baseItem.menu_level);
    var levelPricingMap = parsePricingMap(levelPrice);
    var matrixPricingMap = parsePricingMap(baseItem.matrix_price);
    var resolvedMatrixWeight = parseNumericValue(matrixWeight);
    var usesMatrixPricing = Number(baseItem.is_matrix) > 0;
    if (baseItemPrice === null || baseItemLevel === null || levelPricingMap === null) {
        return null;
    }
    var modifierPriceForBaseItemLevel = getPriceByKey(levelPricingMap, baseItemLevel);
    if (modifierPriceForBaseItemLevel === null) {
        return null;
    }
    if (!usesMatrixPricing) {
        return baseItemPrice + modifierPriceForBaseItemLevel;
    }
    if (!matrixPricingMap) {
        return null;
    }
    if (resolvedMatrixWeight !== null && resolvedMatrixWeight > 0) {
        var matrixTotal = getPriceByKey(matrixPricingMap, resolvedMatrixWeight);
        return matrixTotal !== null ? matrixTotal : null;
    }
    var matrixBase = getPriceByKey(matrixPricingMap, 0);
    if (matrixBase === null) {
        return null;
    }
    return matrixBase + modifierPriceForBaseItemLevel;
};
export var MenuItemCard = function (_a) {
    var _b, _c;
    var imageUrl = _a.imageUrl, label = _a.label, levelPrice = _a.levelPrice, matrixWeight = _a.matrixWeight, itemGroup = _a.itemGroup, menu_id = _a.menu_id, isCore = _a.isCore, onCallToAction = _a.onCallToAction, className = _a.className, _d = _a.disabled, disabled = _d === void 0 ? false : _d;
    var marketingData = useMarketing().marketingData;
    var sessionItemNavigation = resolveSessionItemNavigation(menu_id);
    var visibilityEntity = typeof isCore === "undefined" ? sessionItemNavigation : { is_core: isCore };
    if (!shouldDisplayMenuEntity(visibilityEntity)) {
        return null;
    }
    var foundMarketingObject = (_c = (_b = marketingData === null || marketingData === void 0 ? void 0 : marketingData.marketingMenuItems) === null || _b === void 0 ? void 0 : _b.find(function (item) { return String(item.posMenuId) === String(menu_id); })) !== null && _c !== void 0 ? _c : null;
    var resolvedBasePrice = resolveBasePrice(levelPrice, matrixWeight, itemGroup, menu_id);
    var formattedBasePrice = resolvedBasePrice !== null ? formatCurrency(resolvedBasePrice) : null;
    return (React.createElement(RadixButton, { className: "button-reset", onClick: function () { return onCallToAction === null || onCallToAction === void 0 ? void 0 : onCallToAction(menu_id); }, disabled: !onCallToAction || disabled },
        React.createElement("div", { className: ["radix-menu-item-card", className].filter(Boolean).join(" ") },
            React.createElement("div", { className: "radix-menu-item-card__media" }, imageUrl ? (React.createElement("img", { src: imageUrl, alt: label, className: "radix-menu-item-card__image", loading: "lazy" })) : (React.createElement("div", { className: "radix-menu-item-card__placeholder", "aria-label": "No image available" }, "No Image"))),
            React.createElement("div", { className: "radix-menu-item-card__content" },
                React.createElement("div", { className: "radix-menu-item-card__badge-row" }, foundMarketingObject ? (React.createElement("div", { className: "radix-menu-item-card__badge" },
                    React.createElement(RadixBadge, { text: foundMarketingObject.marketingText, backgroundColor: foundMarketingObject.backgroundColor, borderColor: foundMarketingObject.borderColor, fontColor: foundMarketingObject.fontColor, className: "radix-menu-item-card__badge-pill" }))) : null),
                React.createElement("div", { className: "radix-menu-item-card__label font-weight-bold" }, label),
                formattedBasePrice ? (React.createElement("div", { className: "radix-menu-item-card__price" }, formattedBasePrice)) : null),
            React.createElement("div", { className: "radix-menu-item-card__action" },
                React.createElement("span", { className: "radix-menu-item-card__button" },
                    React.createElement(PlusIcon, { className: "radix-menu-item-card_plus_icon" }))))));
};
